import CardCondition from '@/components/CardCondition';
import CardConditionItem from '@/components/CardConditionItem';
import DisabledDivider from '@/components/DisabledDivider';
import { ExcludeRuleConditionStyled } from '@/components/ExcludeRuleCondition/styled';
import LocationCheckoutConditionItem from '@/components/LocationRuleCondition/LocationCheckoutConditionItem';
import ModalAppliedItemsWithTags from '@/components/ModalAppliedItemsWithTags';
import { conditionsToShowException } from '@/constants';
import { AppliedItemTypes, ItemConditionEnum, SortDirection } from '@/constants/enum';
import { checkIsEmptyRequiredDataToShowException } from '@/helpers';
import useOptions from '@/hooks/useOptions';
import useUserPlan from '@/hooks/useUserPlan';
import { clickedDiscardSelector } from '@/redux/slice/appbar.slice';
import checkoutRuleSlice, {
  checkoutRuleErrorsSelector,
  ICheckoutRuleErrorField,
  isDefaultOpenModalAddConditionCheckoutSelector,
} from '@/redux/slice/checkoutRule.slice';
import ruleSlice, { IRuleErrorField, isDefaultOpenModalAddConditionSelector, ruleErrorsSelector } from '@/redux/slice/rule.slice';
import { IOptions, IProductMetafield } from '@/types';
import { IParamsApi } from '@/types/apis/params';
import { IConditionValue, IItemCondition, ILocationCondition, RelationCondition } from '@/types/newSetup.type';
import { Badge, Icon, Select, Text, TextField } from '@shopify/polaris';
import { InfoIcon, ProductAddIcon, ProductIcon } from '@shopify/polaris-icons';
import _debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface IProps {
  onAction: () => void;
  data: IItemCondition[];
  onChange: (key: IItemCondition['type'] | ILocationCondition['type']) => (value: IConditionValue[] | null) => void;
  type: AppliedItemTypes;
  onRemove: (condition: IItemCondition['type'] | ILocationCondition['type']) => void;
  trackRemove?: boolean;
  typeRelationOfItem: RelationCondition;
  isInCheckout?: boolean;
  onRemoveLocationCondition?: (condition: IItemCondition['type'] | ILocationCondition['type']) => void;
  onAddLocationCondition?: (
    key: IItemCondition['type'] | ILocationCondition['type'],
  ) => (value: IConditionValue[] | null) => void;
  dataLocationConditions?: ILocationCondition[];
  isCountInventoryQuantityFromLocations?: boolean;
  handleChangeCountInventoryQuantityFromLocations?: (value: boolean) => void;
}

interface IDataExclude {
  products: IOptions[];
  products_exception: IOptions[];
  collections: IOptions[];
  vendors: IOptions[];
  productTags: IOptions[];
  inventoryLocations: IOptions[];
  SKUs: IOptions[];
  variantsProducts: IParamsApi.IVariantsProduct[];
  variantsExceptionProducts: IParamsApi.IVariantsProduct[];
  productMetafield: IProductMetafield[];
  variantMetafield: IProductMetafield[];
  minInventoryQuantity?: number;
  maxInventoryQuantity?: number;
}

export const mapTypeToTitle = {
  [ItemConditionEnum.ExcludeCollection]: 'Collections exclude',
  [ItemConditionEnum.ExcludeVendor]: 'Vendors exclude',
  [ItemConditionEnum.ExcludeProduct]: 'Products, variants exclude ',
  [ItemConditionEnum.IncludeCollection]: 'Collections include',
  [ItemConditionEnum.IncludeVendor]: 'Vendors include ',
  [ItemConditionEnum.IncludeProduct]: 'Products, variants include',
  [ItemConditionEnum.IncludeSKU]: 'SKUs include',
  [ItemConditionEnum.ExcludeSKU]: 'SKUs exclude',
  [ItemConditionEnum.IncludeProductTag]: 'Product tags include',
  [ItemConditionEnum.ExcludeProductTag]: 'Product tags exclude',
  [ItemConditionEnum.InventoryQuantity]: 'Inventory quantity',
  [ItemConditionEnum.Exception]: 'Exceptions',
  // [ItemConditionEnum.IncludeInventoryLocation]: 'Inventory locations include',
  // [ItemConditionEnum.ExcludeInventoryLocation]: 'Inventory locations exclude',
  // [ItemConditionEnum.IncludeProductMetafield]: 'Product metafields include',
  // [ItemConditionEnum.ExcludeProductMetafield]: 'Product metafields exclude',
  // [ItemConditionEnum.IncludeVariantMetafield]: 'Variant metafields include',
  // [ItemConditionEnum.ExcludeVariantMetafield]: 'Variant metafields exclude',
};

const initSearchValue = {
  searchProduct: '',
  searchCollection: '',
  searchVendor: '',
  searchSKU: '',
  searchTag: '',
  searchInventoryLocation: '',
  searchProductsException: '',
};

const ExcludeRuleCondition = ({
  onAction,
  data,
  onChange,
  type,
  onRemove,
  trackRemove,
  typeRelationOfItem,
  isInCheckout,
  onAddLocationCondition,
  onRemoveLocationCondition,
  dataLocationConditions,
  isCountInventoryQuantityFromLocations,
  handleChangeCountInventoryQuantityFromLocations,
}: IProps) => {
  const dispatch = useDispatch();
  const isDefaultOpenModalAddConditionNormal = useSelector(isDefaultOpenModalAddConditionSelector);
  const isDefaultOpenModalAddConditionInCheckout = useSelector(isDefaultOpenModalAddConditionCheckoutSelector);

  const isDefaultOpenModalAddCondition = useMemo(() => {
    return isInCheckout ? isDefaultOpenModalAddConditionInCheckout : isDefaultOpenModalAddConditionNormal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(isDefaultOpenModalAddConditionInCheckout),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(isDefaultOpenModalAddConditionNormal),
    isInCheckout,
  ]);
  const { isFreePlan } = useUserPlan();

  const errorsNormal = useSelector(ruleErrorsSelector);
  const errorsCheckout = useSelector(checkoutRuleErrorsSelector);

  const errors = useMemo(() => {
    return isInCheckout ? errorsCheckout : errorsNormal;
  }, [errorsCheckout, errorsNormal, isInCheckout]);
  const clickedDiscard = useSelector(clickedDiscardSelector);
  const [initData, setInitData] = useState<boolean>(true);
  const [initDataException, setInitDataException] = useState<boolean>(true);
  const [dataExclude, setDataExclude] = useState<IDataExclude>({
    products: [],
    products_exception: [],
    collections: [],
    vendors: [],
    productTags: [],
    inventoryLocations: [],
    SKUs: [],
    variantsProducts: [],
    variantsExceptionProducts: [],
    productMetafield: [],
    variantMetafield: [],
  });
  const [search, setSearch] = useState<{
    searchProduct: string;
    searchCollection: string;
    searchVendor: string;
    searchProductsException: string;
    searchSKU: string;
    searchTag: string;
    searchInventoryLocation: string;
    includeProductIds?: string;
    includeCollectionIds?: string;
  }>(initSearchValue);

  const currentSlice = useMemo(() => {
    return isInCheckout ? checkoutRuleSlice : ruleSlice;
  }, [isInCheckout]);

  const dataIncludeProduct = useMemo(() => {
    return (
      data
        ?.find((item) => item.type === ItemConditionEnum.IncludeProduct || item.type === ItemConditionEnum.ExcludeProduct)
        ?.value?.map((item) => item?.data)
        ?.toString() || undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  const dataIncludeExceptionProduct = useMemo(() => {
    return (
      data
        ?.find((item) => item.type === ItemConditionEnum.Exception)
        ?.value?.map((item) => item?.data)
        ?.toString() || undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  const dataIncludeCollection = useMemo(() => {
    return (
      data
        ?.find((item) => item.type === ItemConditionEnum.IncludeCollection || item.type === ItemConditionEnum.ExcludeCollection)
        ?.value?.map((item) => item?.data)
        ?.toString() || undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  const dataIncludeTags = useMemo(() => {
    return (
      data
        ?.find((item) => item.type === ItemConditionEnum.IncludeProductTag || item.type === ItemConditionEnum.ExcludeProductTag)
        ?.value?.map((item) => item?.data)
        ?.toString() || undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  const dataIncludeSKUs = useMemo(() => {
    return (
      data
        ?.find((item) => item.type === ItemConditionEnum.IncludeSKU || item.type === ItemConditionEnum.ExcludeSKU)
        ?.value?.map((item) => item?.data)
        ?.toString() || undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);
  // const dataIncludeInventoryLocation = useMemo(() => {
  //   return (
  //     data
  //       ?.find(
  //         (item) =>
  //           item.type === ItemConditionEnum.IncludeInventoryLocation || item.type === ItemConditionEnum.ExcludeInventoryLocation,
  //       )
  //       ?.value?.map((item) => item?.data)
  //       ?.toString() || undefined
  //   );
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [JSON.stringify(data)]);

  const options = useOptions({
    products: {
      name: search.searchProduct,
      includeProductIds: search.searchProduct ? undefined : dataIncludeProduct,
      sortDirection: SortDirection.ASC,
    },
    collections: {
      name: search.searchCollection,
      includeCollectionIds: search.searchCollection ? undefined : dataIncludeCollection,
      sortDirection: SortDirection.ASC,
    },
    productTags: {
      name: search.searchTag,
      includeProductTagsIds: search.searchTag ? undefined : dataIncludeTags,
      sortDirection: SortDirection.ASC,
    },
    productSKUs: {
      name: search.searchSKU,
      includeProductSKUsIds: search.searchTag ? undefined : dataIncludeSKUs,
      sortDirection: SortDirection.ASC,
    },
    vendors: true,
    // inventoryLocations: {
    //   name: search.searchInventoryLocation,
    //   includeInventoryLocationIds: search.searchTag ? undefined : dataIncludeInventoryLocation,
    //   sortDirection: SortDirection.ASC,
    // },
  });

  const exceptionOptions = useOptions({
    products: {
      name: search.searchProductsException,
      includeProductIds: search.searchProductsException ? undefined : dataIncludeExceptionProduct,
      sortDirection: SortDirection.ASC,
    },
  });

  const handleClearInputSearch = useCallback(() => {
    setSearch(initSearchValue);
  }, []);

  const findAndFilterItems = useCallback(
    (conditionTypes: ItemConditionEnum[], optionsKey: keyof typeof options, currentOptions: typeof options) => {
      const appliedItems = data.find((item) => conditionTypes.includes(item.type))?.value?.map((item) => item?.data);
      return (currentOptions[optionsKey] as IOptions[])?.filter((item) => appliedItems?.includes(item.value));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(data)],
  );

  const mapVariants = useCallback(
    (conditionType: ItemConditionEnum, currentOptions: typeof options) => {
      const appliedItems = data.find((item) => item.type === conditionType)?.value;
      return appliedItems?.map((item) => {
        const currentVariantOption = currentOptions?.variantsProducts?.find((v) => v.idProduct === item.data);
        if (currentVariantOption) {
          const currentVariants = item?.variants?.split(',').filter((v) => v);
          return {
            variants: currentVariants?.length
              ? currentVariantOption.variants?.filter((v) => currentVariants?.includes(v.id))
              : currentVariantOption.variants,
            totalVariants: currentVariantOption.variants?.length,
            idProduct: item?.data,
          };
        }
        return item;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(data)],
  );

  const handleSetError = useCallback(
    (field: IRuleErrorField | ICheckoutRuleErrorField, message: string) => (isClear: boolean) => {
      if (isClear) {
        //@ts-ignore-next-line
        dispatch(currentSlice.actions.handleSetError({ value: { field, msg: '' } }));
      } else {
        //@ts-ignore-next-line
        dispatch(currentSlice.actions.handleSetError({ value: { field, msg: message } }));
      }
    },
    [currentSlice.actions, dispatch],
  );

  useEffect(() => {
    setInitData(true);
    setInitDataException(true);
  }, [trackRemove]);

  const inventoryQuantity = useMemo(() => {
    return data.find((item) => item.type === ItemConditionEnum.InventoryQuantity)?.value[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data)]);

  // const productMetafield = useMemo(() => {
  //   return (data.find(
  //     (item) =>
  //       item.type === ItemConditionEnum.IncludeProductMetafield || item.type === ItemConditionEnum.ExcludeProductMetafield,
  //   )?.value || []) as unknown as IProductMetafield[];
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [JSON.stringify(data)]);

  // const variantMetafield = useMemo(() => {
  //   return (data.find(
  //     (item) =>
  //       item.type === ItemConditionEnum.IncludeVariantMetafield || item.type === ItemConditionEnum.ExcludeVariantMetafield,
  //   )?.value || []) as unknown as IProductMetafield[];
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [JSON.stringify(data)]);

  useEffect(() => {
    if (!initData) return;

    const newDataExclude = {
      products: findAndFilterItems(
        [ItemConditionEnum.IncludeProduct, ItemConditionEnum.ExcludeProduct],
        'productOptions',
        options,
      ),
      collections: findAndFilterItems(
        [ItemConditionEnum.IncludeCollection, ItemConditionEnum.ExcludeCollection],
        'collectionOptions',
        options,
      ),
      vendors: findAndFilterItems([ItemConditionEnum.IncludeVendor, ItemConditionEnum.ExcludeVendor], 'vendorOptions', options),
      variantsProducts:
        mapVariants(ItemConditionEnum.IncludeProduct, options) || mapVariants(ItemConditionEnum.ExcludeProduct, options),
      productTags: findAndFilterItems(
        [ItemConditionEnum.IncludeProductTag, ItemConditionEnum.ExcludeProductTag],
        'productTagOptions',
        options,
      ),
      SKUs: findAndFilterItems([ItemConditionEnum.IncludeSKU, ItemConditionEnum.ExcludeSKU], 'productSKUOptions', options),
      minInventoryQuantity: inventoryQuantity?.minInventoryQuantity,
      maxInventoryQuantity: inventoryQuantity?.maxInventoryQuantity,
      // inventoryLocations: findAndFilterItems(
      //   [ItemConditionEnum.IncludeInventoryLocation, ItemConditionEnum.ExcludeInventoryLocation],
      //   'inventoryLocationOptions',
      //   options,
      // ),
      // productMetafield,
      // variantMetafield,
    };

    setDataExclude((prev) => ({ ...prev, ...newDataExclude } as IDataExclude));
    if ((newDataExclude && !!search.searchProduct) || !!search.searchCollection) setInitData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    findAndFilterItems,
    mapVariants,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(options),
    initData,
    inventoryQuantity,
    // // eslint-disable-next-line react-hooks/exhaustive-deps
    // JSON.stringify(productMetafield),
    // // eslint-disable-next-line react-hooks/exhaustive-deps
    // JSON.stringify(variantMetafield),
  ]);

  useEffect(() => {
    if (!initDataException || !exceptionOptions.products) return;

    const newDataExclude = {
      products_exception: findAndFilterItems([ItemConditionEnum.Exception], 'productOptions', exceptionOptions),
      variantsExceptionProducts: mapVariants(ItemConditionEnum.Exception, exceptionOptions),
    };

    setDataExclude((prev) => ({ ...prev, ...newDataExclude } as IDataExclude));
    if (newDataExclude && !!search.searchProductsException) setInitDataException(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(exceptionOptions), findAndFilterItems, mapVariants, initDataException]);

  useEffect(() => {
    if (clickedDiscard) {
      setInitData(true);
      setInitDataException(true);
    }
  }, [clickedDiscard]);

  const listMainCondition = useMemo(() => {
    return data.filter((item) => item.type !== ItemConditionEnum.Exception);
  }, [data]);

  const listConditionCombine = useMemo(() => {
    const tempItemCondition = listMainCondition?.map((item) => ({ ...item, conditionType: 'itemCondition' })) || [];
    const tempLocationCondition = dataLocationConditions?.map((item) => ({ ...item, conditionType: 'locationCondition' })) || [];
    return [...tempItemCondition, ...tempLocationCondition].sort((a, b) => (a.order || 0) - (b.order || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(listMainCondition), JSON.stringify(dataLocationConditions)]);

  const exceptionProduct = useMemo(() => {
    return data.find((item) => item.type === ItemConditionEnum.Exception);
  }, [data]);

  const exceptionProductOptions = useMemo(() => {
    return exceptionOptions.productOptions?.filter(
      (item) => !dataExclude.products.some((product) => product.value === item.value),
    );
  }, [dataExclude.products, exceptionOptions.productOptions]);

  useEffect(() => {
    const isHasConditionToEnableException = data.some((item) => conditionsToShowException.includes(item.type));

    const isEmptyRequiredData = checkIsEmptyRequiredDataToShowException(data);

    if (!isHasConditionToEnableException || isEmptyRequiredData) {
      onChange(ItemConditionEnum.Exception)(null);
      handleSetError('exceptionProduct', '')(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data), JSON.stringify(listMainCondition), JSON.stringify(onChange), handleSetError]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceHandleSearch = useCallback(
    _debounce((newState: typeof search) => {
      setSearch(newState);
    }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      JSON.stringify(search),
      JSON.stringify(dataExclude?.collections),
      JSON.stringify(dataExclude?.vendors),
      JSON.stringify(dataExclude?.products),
    ],
  );

  const handleGenKey = useCallback(
    (key: keyof typeof dataExclude | 'products_exception') => {
      if (key === 'products_exception' || key === 'minInventoryQuantity' || key === 'maxInventoryQuantity') {
        return key;
      }
      let tempKey = key as string;
      switch (key) {
        case 'productTags':
          tempKey = 'tags';
          break;
        case 'inventoryLocations':
          tempKey = 'inventory_locations';
          break;
        case 'productMetafield':
          tempKey = 'metafields_products';
          break;
        case 'variantMetafield':
          tempKey = 'metafields_variants';
          break;
        case 'SKUs':
          tempKey = 'skus';
          break;
        default:
          break;
      }
      if (type === AppliedItemTypes.Include) {
        return `${tempKey}_applied`;
      } else if (type === AppliedItemTypes.Exclude) {
        return `${tempKey}_excluded`;
      } else {
        return tempKey;
      }
    },
    [type],
  );

  const handleChangeExcludeData = useCallback(
    (key: keyof typeof dataExclude | 'products_exception', value: any, variants?: IParamsApi.IVariantsProduct[]) => {
      const newData = { ...dataExclude, [key]: value };
      if (variants) {
        if (key === 'products_exception') {
          newData.variantsExceptionProducts = variants;
        } else {
          newData.variantsProducts = variants;
        }
      }
      setDataExclude(newData);
      const newDataItemConditions = data.map((item) => {
        const newKey = handleGenKey(key);
        let data: any[] = [];
        if (variants) {
          data = variants.map((item) => {
            let variants = item.variants;
            if (variants?.length === item.totalVariants) {
              variants = [];
            }
            return {
              data: item.idProduct,
              variants: variants?.length ? variants.map((item) => item.id).toString() : '',
            };
          });
        } else {
          data = Array.isArray(value)
            ? value.map((item: any) => {
                if (key === 'productMetafield' || key === 'variantMetafield') {
                  return {
                    key: item.key,
                    data: item.data,
                    label: item.label,
                    type: item.type,
                  };
                }
                return {
                  data: item.value,
                };
              })
            : value;
        }
        if (item.type === newKey) {
          return { ...item, value: data };
        } else if (item.type === 'inventory_quantity') {
          return {
            ...item,
            type: 'inventory_quantity',
            value: [
              {
                minInventoryQuantity: item.value[0]?.minInventoryQuantity,
                maxInventoryQuantity: item.value[0]?.maxInventoryQuantity,
                data: 'inventory_quantity',
                [newKey]: value,
              },
            ],
          };
        }
        return item;
      });
      dispatch(
        currentSlice.actions.handleChange({
          //@ts-ignore-next-line
          key: isInCheckout ? 'checkoutItemConditions' : 'itemConditions',
          value: newDataItemConditions as IItemCondition[],
        }),
      );
    },
    [currentSlice.actions, data, dataExclude, dispatch, handleGenKey, isInCheckout],
  );

  const renderAction = useCallback(
    (type: IItemCondition['type']) => {
      switch (type) {
        case ItemConditionEnum.ExcludeProduct:
        case ItemConditionEnum.IncludeProduct:
          const productOptions = options.productOptions.filter(
            (item) => !dataExclude.products_exception.some((product) => product.value === item.value),
          );
          return (
            <ModalAppliedItemsWithTags
              isInCheckout={isInCheckout}
              key={type}
              buttonAndModalTitle="Select"
              footerCountText="variant(s) selected"
              options={productOptions}
              tag={(dataExclude?.products as IOptions[]) || []}
              selectedOptions={(dataExclude?.products as IOptions[]) || []}
              setSelectedOptions={(value, variants) => {
                handleChangeExcludeData('products', value, variants);
              }}
              handleSearch={(value: string) => debounceHandleSearch({ ...search, searchProduct: value })}
              isRequired={false}
              disabled={false}
              variantsProducts={options.variantsProducts}
              selectedVariantsProduct={(dataExclude?.variantsProducts as IParamsApi.IVariantsProduct[]) || []}
              handleSetError={handleSetError('product', 'Please select at least 1 product or variant')}
              error={errors.find((item) => item.field === 'product')}
              handleClearInputSearch={handleClearInputSearch}
              desc="Only the first 25 products from A-Z are displayed. Search product name if not fully loaded."
              isDefaultOpen={!!isDefaultOpenModalAddCondition?.product}
            />
          );
        case ItemConditionEnum.IncludeCollection:
        case ItemConditionEnum.ExcludeCollection:
          return (
            <ModalAppliedItemsWithTags
              isInCheckout={isInCheckout}
              key={type}
              buttonAndModalTitle="Select"
              footerCountText="collection(s) selected"
              options={options.collectionOptions}
              tag={(dataExclude?.collections as IOptions[]) || []}
              selectedOptions={(dataExclude?.collections as IOptions[]) || []}
              setSelectedOptions={(value) => {
                handleChangeExcludeData('collections', value);
              }}
              handleSearch={(value: string) => debounceHandleSearch({ ...search, searchCollection: value })}
              isRequired={false}
              disabled={false}
              handleSetError={handleSetError('collection', 'Please select at least 1 collection')}
              error={errors.find((item) => item.field === 'collection')}
              handleClearInputSearch={handleClearInputSearch}
              desc="Only the first 25 collections from A-Z are displayed. Search collection name if not fully loaded."
              isDefaultOpen={!!isDefaultOpenModalAddCondition?.collection}
            />
          );
        case ItemConditionEnum.IncludeVendor:
        case ItemConditionEnum.ExcludeVendor:
          return (
            <ModalAppliedItemsWithTags
              isInCheckout={isInCheckout}
              key={type}
              buttonAndModalTitle="Select"
              footerCountText="vendor(s) selected"
              options={options.vendorOptions}
              tag={(dataExclude?.vendors as IOptions[]) || []}
              selectedOptions={(dataExclude?.vendors as IOptions[]) || []}
              setSelectedOptions={(value) => {
                handleChangeExcludeData('vendors', value);
              }}
              isRequired={false}
              disabled={false}
              handleSetError={handleSetError('vendor', 'Please select at least 1 vendor')}
              error={errors.find((item) => item.field === 'vendor')}
              handleClearInputSearch={handleClearInputSearch}
              desc="Only the first 25 vendors from A-Z are displayed. Search vendor name if not fully loaded."
              isDefaultOpen={!!isDefaultOpenModalAddCondition?.vendor}
            />
          );
        case ItemConditionEnum.IncludeProductTag:
        case ItemConditionEnum.ExcludeProductTag:
          return (
            <ModalAppliedItemsWithTags
              isInCheckout={isInCheckout}
              key={type}
              buttonAndModalTitle="Select"
              footerCountText="product tag(s) selected"
              options={options.productTagOptions}
              tag={(dataExclude?.productTags as IOptions[]) || []}
              selectedOptions={(dataExclude?.productTags as IOptions[]) || []}
              setSelectedOptions={(value) => {
                handleChangeExcludeData('productTags', value);
              }}
              isRequired={false}
              disabled={false}
              handleSetError={handleSetError('productTags', 'Please select at least 1 product tag')}
              error={errors.find((item) => item.field === 'productTags')}
              handleClearInputSearch={handleClearInputSearch}
              desc="Only the first 25 tags from A-Z are displayed. Search product tag name if not fully loaded."
              isDefaultOpen={!!isDefaultOpenModalAddCondition?.productTags}
              handleSearch={(value: string) => debounceHandleSearch({ ...search, searchTag: value })}
            />
          );
        // case ItemConditionEnum.IncludeInventoryLocation:
        // case ItemConditionEnum.ExcludeInventoryLocation:
        //   return (
        //     <ModalAppliedItemsWithTags
        //       isInCheckout={isInCheckout}
        //       key={type}
        //       buttonAndModalTitle="Select"
        //       footerCountText="inventory location(s) selected"
        //       options={options.inventoryLocationOptions}
        //       tag={(dataExclude?.inventoryLocations as IOptions[]) || []}
        //       selectedOptions={(dataExclude?.inventoryLocations as IOptions[]) || []}
        //       setSelectedOptions={(value) => {
        //         handleChangeExcludeData('inventoryLocations', value);
        //       }}
        //       isRequired={false}
        //       disabled={false}
        //       handleSetError={handleSetError('inventoryLocations', 'Please select at least 1 inventory location')}
        //       error={errors.find((item) => item.field === 'inventoryLocations')}
        //       handleClearInputSearch={handleClearInputSearch}
        //       desc="Only the first 25 locations from A-Z are displayed. Search inventory locations name if not fully loaded."
        //       isDefaultOpen={!!isDefaultOpenModalAddCondition?.inventoryLocations}
        //       handleSearch={(value: string) => debounceHandleSearch({ ...search, searchInventoryLocation: value })}
        //     />
        //   );
        case ItemConditionEnum.IncludeSKU:
        case ItemConditionEnum.ExcludeSKU:
          return (
            <ModalAppliedItemsWithTags
              isInCheckout={isInCheckout}
              key={type}
              buttonAndModalTitle="Select"
              footerCountText="SKU(s) selected"
              options={options.productSKUOptions}
              tag={(dataExclude?.SKUs as IOptions[]) || []}
              selectedOptions={(dataExclude?.SKUs as IOptions[]) || []}
              setSelectedOptions={(value) => {
                handleChangeExcludeData('SKUs', value);
              }}
              isRequired={false}
              disabled={false}
              handleSetError={handleSetError('skus', 'Please select at least 1 SKU')}
              error={errors.find((item) => item.field === 'skus')}
              handleClearInputSearch={handleClearInputSearch}
              desc="Only the first 25 SKUs from A-Z are displayed. Search SKU name if not fully loaded."
              isDefaultOpen={!!isDefaultOpenModalAddCondition?.skus}
              handleSearch={(value: string) => debounceHandleSearch({ ...search, searchSKU: value })}
            />
          );
        // case ItemConditionEnum.IncludeProductMetafield:
        // case ItemConditionEnum.ExcludeProductMetafield:
        //   return (
        //     <CardProductMetafield
        //       handleChangeData={(value) => {
        //         handleChangeExcludeData('productMetafield', value);
        //       }}
        //       data={dataExclude?.productMetafield}
        //       handleSetError={handleSetError('productMetafield', 'Please select at least 1 metafield')}
        //       error={errors.find((item) => item.field === 'productMetafield')?.msg}
        //       isProductMetafield
        //     />
        //   );
        // case ItemConditionEnum.IncludeVariantMetafield:
        // case ItemConditionEnum.ExcludeVariantMetafield:
        //   return (
        //     <CardProductMetafield
        //       handleChangeData={(value) => {
        //         handleChangeExcludeData('variantMetafield', value);
        //       }}
        //       data={dataExclude?.variantMetafield}
        //       handleSetError={handleSetError('variantMetafield', 'Please select at least 1 metafield')}
        //       error={errors.find((item) => item.field === 'variantMetafield')?.msg}
        //     />
        //   );
        default:
          return null;
      }
    },
    [
      options.productOptions,
      options.variantsProducts,
      options.collectionOptions,
      options.vendorOptions,
      options.productTagOptions,
      options.productSKUOptions,
      isInCheckout,
      dataExclude?.products,
      dataExclude?.variantsProducts,
      dataExclude?.collections,
      dataExclude?.vendors,
      dataExclude?.productTags,
      dataExclude?.SKUs,
      dataExclude.products_exception,
      handleSetError,
      errors,
      handleClearInputSearch,
      isDefaultOpenModalAddCondition?.product,
      isDefaultOpenModalAddCondition?.collection,
      isDefaultOpenModalAddCondition?.vendor,
      isDefaultOpenModalAddCondition?.productTags,
      isDefaultOpenModalAddCondition?.skus,
      handleChangeExcludeData,
      debounceHandleSearch,
      search,
    ],
  );

  const handleRemoveCondition = (type: IItemCondition['type']) => () => {
    onRemove(type);
  };

  useEffect(() => {
    if (inventoryQuantity) {
      if (!inventoryQuantity?.maxInventoryQuantity || !inventoryQuantity?.minInventoryQuantity) {
        handleSetError('inventoryQuantity', 'Please enter minimum and maximum value')(false);
      } else if (+inventoryQuantity?.minInventoryQuantity > +inventoryQuantity?.maxInventoryQuantity) {
        handleSetError('inventoryQuantity', 'Minimum value must be less than maximum value')(false);
      } else if (errors.find((item) => item.field === 'inventoryQuantity')) {
        handleSetError('inventoryQuantity', '')(true);
      }
    } else if (errors.find((item) => item.field === 'inventoryQuantity')) {
      handleSetError('inventoryQuantity', '')(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(errors), handleSetError, JSON.stringify(inventoryQuantity)]);

  const isShowSelectRelation = useMemo(() => {
    return (
      (!isInCheckout && listMainCondition?.length > 1) ||
      (isInCheckout &&
        ((listMainCondition?.length && dataLocationConditions?.length) ||
          listMainCondition?.length > 1 ||
          (dataLocationConditions?.length || 0) > 1))
    );
  }, [dataLocationConditions?.length, isInCheckout, listMainCondition?.length]);

  // const isShowCountInventoryQuantityFromLocations = useMemo(() => {
  //   const isHasInventoryLocation = listMainCondition?.some((item) =>
  //     [ItemConditionEnum.ExcludeInventoryLocation, ItemConditionEnum.IncludeInventoryLocation].includes(item.type),
  //   );
  //   const isTypeAnd = typeRelationOfItem === RelationCondition.AND;
  //   return isHasInventoryLocation && isTypeAnd;
  // }, [listMainCondition, typeRelationOfItem]);

  return (
    <ExcludeRuleConditionStyled>
      <CardCondition
        icon={ProductAddIcon}
        title={isInCheckout ? '' : 'Item conditions'}
        badgeContent={isFreePlan ? null : `${data?.length} condition${data?.length > 1 ? 's' : ''}`}
        badgeTone={isFreePlan ? 'info' : undefined}
        subTitle="Include and/or exclude specific products, collections, vendors, SKUs, tags, inventory locations."
        buttonText="Add condition"
        onAction={onAction}
        collapsible={!isInCheckout && data.length > 0}
        defaultExpanded={data.length > 0}
        isShowButtonOnBottom
        textLimitedPlan={isFreePlan ? 'Standard and above' : undefined}
      >
        {isInCheckout && !listMainCondition?.length && !dataLocationConditions?.length && (
          <p className="mb-16">Include and/or exclude specific products, collections, vendors, SKUs, tags.</p>
        )}
        {isShowSelectRelation ? (
          <div className={`d-flex align-center gap-8 ${!isInCheckout && 'mt-16'}`}>
            <span>Logic</span>
            <Select
              options={[
                { label: 'AND', value: RelationCondition.AND },
                { label: 'OR', value: RelationCondition.OR },
              ]}
              label=""
              labelHidden
              value={typeRelationOfItem}
              onChange={(value) => {
                dispatch(currentSlice.actions.handleChange({ key: 'typeRelationOfItemCondition', value }));
              }}
            />
            <span>{typeRelationOfItem === RelationCondition.AND ? 'All' : 'Any'} conditions match</span>
          </div>
        ) : null}
        <div className="main-condition-container mt-16">
          {listConditionCombine?.map((item, index) => {
            if (item.conditionType === 'locationCondition') {
              return (
                <React.Fragment key={index}>
                  {isInCheckout && dataLocationConditions && onAddLocationCondition && onRemoveLocationCondition && (
                    <LocationCheckoutConditionItem
                      data={item as ILocationCondition}
                      onChange={onAddLocationCondition}
                      onRemove={onRemoveLocationCondition}
                      isInCheckout={isInCheckout}
                      typeRelationOfItem={typeRelationOfItem}
                      isShowBadge={index < listConditionCombine.length - 1 && listConditionCombine.length > 1}
                    />
                  )}
                </React.Fragment>
              );
            } else {
              return (
                <div className="mt-16" key={item.type}>
                  {item.type !== ItemConditionEnum.InventoryQuantity ? (
                    <CardConditionItem
                      onDismiss={handleRemoveCondition((item as IItemCondition).type)}
                      title={
                        <div className="d-flex align-center gap-8">
                          <span>{mapTypeToTitle[(item as IItemCondition).type]}</span>
                          <Badge>{item.value?.filter((item) => item.data)?.length + ''}</Badge>
                        </div>
                      }
                      collapsible
                      defaultExpanded={item.value?.length > 0}
                    >
                      {renderAction((item as IItemCondition).type)}
                    </CardConditionItem>
                  ) : (
                    <CardConditionItem
                      onDismiss={handleRemoveCondition(item.type)}
                      title={
                        <div className="d-flex align-center gap-8">
                          <span>{mapTypeToTitle[item.type]}</span>
                        </div>
                      }
                      collapsible
                      defaultExpanded={item.value?.length > 0}
                    >
                      <div className="d-flex flex-column justify-center gap-12 mt-8">
                        <span style={{ fontSize: '13px' }}>Total available quantity in all locations</span>
                        <div className="items-center d-flex gap-16">
                          <div className="w-full">
                            <TextField
                              value={dataExclude?.minInventoryQuantity ? dataExclude?.minInventoryQuantity.toString() : ''}
                              onChange={(value) => handleChangeExcludeData('minInventoryQuantity', value)}
                              label={<b style={{ fontSize: '13px' }}>Minimum</b>}
                              autoComplete="off"
                              type="number"
                              min={0}
                              requiredIndicator={true}
                            />
                          </div>
                          <div className="w-full">
                            <TextField
                              value={dataExclude?.maxInventoryQuantity ? dataExclude?.maxInventoryQuantity.toString() : ''}
                              onChange={(value) => handleChangeExcludeData('maxInventoryQuantity', value)}
                              label={<b style={{ fontSize: '13px' }}>Maximum</b>}
                              autoComplete="off"
                              type="number"
                              min={0}
                              requiredIndicator={true}
                            />
                          </div>
                        </div>
                        {errors.find((item) => item.field === 'inventoryQuantity') && (
                          <div className="d-flex align-center gap-4">
                            <Icon source={InfoIcon} tone="critical" />
                            <Text as="p" variant="bodyMd" tone="critical">
                              {errors.find((item) => item.field === 'inventoryQuantity')?.msg}
                            </Text>
                          </div>
                        )}
                        {/* {isShowCountInventoryQuantityFromLocations ? (
                          <CustomSwitchLayout
                            content={`When using 'Inventory locations' condition with 'AND' logic, only total available quantity from those locations will be counted.`}
                            title="Count inventory only from selected locations"
                            enable={isCountInventoryQuantityFromLocations}
                            onChange={handleChangeCountInventoryQuantityFromLocations}
                          />
                        ) : null} */}
                      </div>
                    </CardConditionItem>
                  )}
                  {listConditionCombine?.length > 1 && index !== listConditionCombine?.length - 1 ? (
                    <div className="mt-16">
                      <Badge tone="info">{typeRelationOfItem.toLocaleUpperCase()}</Badge>
                    </div>
                  ) : null}
                </div>
              );
            }
          })}
        </div>

        {!!exceptionProduct && (
          <div className="mt-16">
            <div className="mb-16">
              <DisabledDivider />
            </div>
            <CardConditionItem
              onDismiss={handleRemoveCondition(exceptionProduct.type)}
              title={
                <div className="d-flex align-center gap-8">
                  <span style={{ color: '#8e1f0b' }}>{mapTypeToTitle[exceptionProduct.type]}</span>
                  <Badge>{exceptionProduct.value?.filter((item) => item.data)?.length + ''}</Badge>
                </div>
              }
            >
              <p className="mb-8">
                These products are <b>unaffected</b> by above Collection or Vendor condition.
              </p>
              <ModalAppliedItemsWithTags
                isInCheckout={isInCheckout}
                key={type}
                buttonAndModalTitle="Select products"
                buttonIcon={ProductIcon}
                footerCountText="variant(s) selected"
                options={exceptionProductOptions}
                tag={(dataExclude?.products_exception as IOptions[]) || []}
                selectedOptions={(dataExclude?.products_exception as IOptions[]) || []}
                setSelectedOptions={(value, variants) => {
                  handleChangeExcludeData('products_exception', value, variants);
                }}
                handleSearch={(value: string) => debounceHandleSearch({ ...search, searchProductsException: value })}
                isRequired={false}
                disabled={false}
                variantsProducts={exceptionOptions.variantsProducts}
                selectedVariantsProduct={(dataExclude?.variantsExceptionProducts as IParamsApi.IVariantsProduct[]) || []}
                handleSetError={handleSetError('exceptionProduct', 'Please select at least 1 product or variant')}
                error={errors.find((item) => item.field === 'exceptionProduct')}
                handleClearInputSearch={handleClearInputSearch}
                desc="Only the first 25 products from A-Z are displayed. Search product name if not fully loaded."
                isDefaultOpen={!!isDefaultOpenModalAddCondition?.exception}
              />
            </CardConditionItem>
          </div>
        )}
      </CardCondition>
    </ExcludeRuleConditionStyled>
  );
};

export default ExcludeRuleCondition;
